/**
 * @fileoverview Sales Team page.
 * @description The caller's own Sales team from the backend (Confluence 3.5.3.13 and 3.5.3.14):
 * the table is GET /users/me/team — the caller themself plus everyone who reports to them,
 * directly or indirectly (just themself for a Sales Executive; every Sales member for a Super
 * Admin) — and the KPI cards are GET /users/me/team/summary, which ignore the table's filters. The
 * backend decides who is in the team and does the filtering: the search is debounced, and the
 * Role and Reports To choices come from GET /users/filter-options in the MY_TEAM context. Each row
 * shows the member's employee ID, role, manager, status and the banks they own directly (never a
 * roll-up of their reports); "Banks covered" is every bank the team owns, each once. Clicking a
 * row, or its chevron, expands its contact and joining details. A read-only view: no account
 * administration happens here (that is User Management).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PageLoadError,
  TableEmptyState,
  TableFooter,
  TableSearchInput,
  TableSkeleton,
} from '@/features/sales'
import {
  DIRECTORY_STATUS_LABEL_KEY,
  DIRECTORY_STATUS_VARIANT,
  formatJoiningDate,
  formatPhone,
  useMyTeam,
  useMyTeamSummary,
  useUserFilterOptions,
} from '@/features/users'
import Badge from '@/shared/components/atoms/Badge'
import Select from '@/shared/components/atoms/Select'
import KpiStrip from '@/shared/components/molecules/KpiStrip'
import KpiStripSkeleton from '@/shared/components/molecules/KpiStripSkeleton'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

import type { DirectoryStatus, ListUsersFilters, TeamMember } from '@/features/users'

const ALL = ''
const EMPTY_MEMBERS: TeamMember[] = []
const STATUS_FILTERS: DirectoryStatus[] = ['ACTIVE', 'INACTIVE', 'PENDING']
const GRID_COLUMNS =
  'grid-cols-[minmax(220px,2.2fr)_minmax(90px,1fr)_minmax(190px,1.4fr)_minmax(130px,1.2fr)_minmax(120px,1fr)_minmax(130px,1.1fr)_32px]'
const TABLE_MIN_WIDTH = 'min-w-[1040px]'

/**
 * @description Builds a member's 2-letter initials from their name.
 * @param {string} name - The member's display name.
 * @returns {string} Up to two uppercase initials.
 */
function initialsFromName(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * @description Builds GET /users/me/team's filters from the table's controls, leaving out every
 * "All".
 * @param {string} search - The debounced search text.
 * @param {string} roleId - The Role filter.
 * @param {string} managerId - The Reports To filter.
 * @param {DirectoryStatus | ''} status - The Status filter.
 * @returns {ListUsersFilters} The filters.
 */
function toFilters(
  search: string,
  roleId: string,
  managerId: string,
  status: DirectoryStatus | '',
): ListUsersFilters {
  return {
    ...(search && { search }),
    ...(roleId && { roleId }),
    ...(managerId && { reportingManagerUserId: managerId }),
    ...(status && { directoryStatus: status }),
  }
}

/**
 * @description A small briefcase icon for the assigned-banks count.
 */
function BriefcaseIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-primary"
      aria-hidden="true"
    >
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  )
}

interface MemberRowProps {
  member: TeamMember
  expanded: boolean
  onToggle: () => void
}

/**
 * @description One team member's row, with its expandable details.
 * @param {MemberRowProps} props - The member and its expand state.
 * @param {TeamMember} props.member - The member.
 * @param {boolean} props.expanded - Whether the details are open.
 * @param {() => void} props.onToggle - Opens or closes the details.
 */
function MemberRow({ member, expanded, onToggle }: MemberRowProps) {
  const { t } = useTranslation()
  const rowId = member.userId
  const expandLabelKey = expanded
    ? 'sales.salesTeam.collapseRowFor'
    : 'sales.salesTeam.expandRowFor'

  return (
    <div className="border-b border-row-border" data-testid={`my-team-row-${rowId}`}>
      <div
        onClick={onToggle}
        className={`grid ${GRID_COLUMNS} cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-surface-hover sm:px-5`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
            {initialsFromName(member.displayName)}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="truncate text-[13.5px] font-semibold text-ink"
                data-testid={`my-team-member-name-${rowId}`}
              >
                {member.displayName}
              </span>
              {member.isSelf && (
                <span
                  className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-primary"
                  data-testid={`my-team-member-you-badge-${rowId}`}
                >
                  {t('sales.salesTeam.you')}
                </span>
              )}
            </span>
            <span
              className="truncate text-xs text-footer"
              data-testid={`my-team-member-email-${rowId}`}
            >
              {member.email}
            </span>
          </div>
        </div>
        <span
          className="truncate text-[13px] text-cell"
          data-testid={`my-team-employee-code-${rowId}`}
        >
          {member.employeeCode ?? '—'}
        </span>
        <span>
          <Badge
            variant={member.primaryPosition.code === 'SALES_EXECUTIVE' ? 'neutral' : 'info'}
            data-testid={`my-team-role-badge-${rowId}`}
          >
            {member.primaryPosition.name}
          </Badge>
        </span>
        <span
          className="truncate text-[13px] text-cell"
          data-testid={`my-team-reports-to-${rowId}`}
        >
          {member.reportingManager?.displayName ?? '—'}
        </span>
        <span
          className="flex items-center gap-1.5 text-[13.5px] font-bold text-ink"
          data-testid={`my-team-assigned-banks-${rowId}`}
        >
          <BriefcaseIcon />
          {member.workload.currentAssignedTenants}
        </span>
        <span>
          <Badge
            variant={DIRECTORY_STATUS_VARIANT[member.directoryStatus]}
            dot
            data-testid={`my-team-status-badge-${rowId}`}
          >
            {t(DIRECTORY_STATUS_LABEL_KEY[member.directoryStatus])}
          </Badge>
        </span>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onToggle()
          }}
          aria-expanded={expanded}
          data-testid={`my-team-expand-button-${rowId}`}
          aria-label={t(expandLabelKey, { name: member.displayName })}
          className="flex h-8 w-8 shrink-0 justify-self-end cursor-pointer items-center justify-center rounded-lg border border-divider bg-white hover:border-border-hover"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            className={`text-slate transition-transform ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      {expanded && (
        <div
          className="bg-surface-hover px-4 pb-4 pt-1 sm:px-5"
          data-testid={`my-team-details-${rowId}`}
        >
          <dl className="grid grid-cols-1 gap-3.5 rounded-lg border border-row-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                id: 'phone',
                label: t('sales.salesTeam.detail.phone'),
                value: formatPhone(member) || '—',
              },
              {
                id: 'department',
                label: t('sales.salesTeam.detail.department'),
                value: member.department?.name ?? '—',
              },
              {
                id: 'joining-date',
                label: t('sales.salesTeam.detail.doj'),
                value: formatJoiningDate(member.dateOfJoining) || '—',
              },
              {
                id: 'own-banks',
                label: t('sales.salesTeam.detail.ownBanks'),
                value: String(member.workload.currentAssignedTenants),
              },
            ].map((item) => (
              <div
                key={item.id}
                className="flex flex-col gap-0.5"
                data-testid={`my-team-detail-${item.id}-${rowId}`}
              >
                <dt
                  className="text-[10.5px] font-semibold tracking-[0.7px] text-footer"
                  data-testid={`my-team-detail-${item.id}-${rowId}-label`}
                >
                  {item.label}
                </dt>
                <dd
                  className="text-[13px] font-medium text-ink"
                  data-testid={`my-team-detail-${item.id}-${rowId}-value`}
                >
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  )
}

/**
 * @component
 */
function SalesTeamPage() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [roleId, setRoleId] = useState(ALL)
  const [managerId, setManagerId] = useState(ALL)
  const [status, setStatus] = useState<DirectoryStatus | ''>(ALL)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const search = useDebouncedValue(query.trim())

  const teamQuery = useMyTeam(toFilters(search, roleId, managerId, status))
  const summaryQuery = useMyTeamSummary()
  const filterOptions = useUserFilterOptions('MY_TEAM')
  const members = teamQuery.data ?? EMPTY_MEMBERS
  const summary = summaryQuery.data

  const anyFilter = query.trim() !== '' || roleId !== ALL || managerId !== ALL || status !== ALL

  /**
   * @description Resets every table filter back to its default.
   */
  const clearFilters = () => {
    setQuery('')
    setRoleId(ALL)
    setManagerId(ALL)
    setStatus(ALL)
  }

  const header = (
    <PageHeader title={t('nav.salesTeam')} description={t('sales.salesTeam.description')} />
  )

  if (teamQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <KpiStripSkeleton />
        <TableSkeleton columns={7} />
      </div>
    )
  }

  if (teamQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <PageLoadError message={t('sales.salesTeam.loadError')} testId="my-team-load-error" />
      </div>
    )
  }

  return (
    <div className="flex grow flex-col gap-4">
      {header}

      {summaryQuery.isPending ? (
        <KpiStripSkeleton />
      ) : (
        <KpiStrip
          tiles={[
            {
              label: t('sales.salesTeam.kpi.teamSize'),
              sublabel: t('sales.salesTeam.kpi.teamSizeSublabel'),
              icon: 'users',
              iconTone: 'navy',
              value: summary?.teamSize ?? '—',
              testId: 'my-team-kpi-team-size',
            },
            {
              label: t('sales.salesTeam.kpi.managers'),
              sublabel: t('sales.salesTeam.kpi.managersSublabel'),
              icon: 'briefcase',
              iconTone: 'purple',
              value: summary?.salesManagers ?? '—',
              testId: 'my-team-kpi-managers',
            },
            {
              label: t('sales.salesTeam.kpi.executives'),
              sublabel: t('sales.salesTeam.kpi.executivesSublabel'),
              icon: 'userCheck',
              iconTone: 'amber',
              value: summary?.salesExecutives ?? '—',
              testId: 'my-team-kpi-executives',
            },
            {
              label: t('sales.salesTeam.kpi.banksCovered'),
              sublabel: t('sales.salesTeam.kpi.banksCoveredSublabel'),
              icon: 'grid',
              iconTone: 'green',
              value: summary?.assignedTenants ?? '—',
              testId: 'my-team-kpi-banks-covered',
            },
          ]}
        />
      )}

      <section className="flex grow flex-col rounded-[10px] border border-divider bg-white">
        <div className="flex flex-wrap items-center gap-2.5 p-3.5 sm:p-4">
          <TableSearchInput
            value={query}
            onChange={setQuery}
            placeholder={t('sales.salesTeam.searchPlaceholder')}
            data-testid="my-team-search-input"
          />
          <Select
            aria-label={t('sales.salesTeam.filters.allRoles')}
            data-testid="my-team-role-select"
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
            options={[
              { value: ALL, label: t('sales.salesTeam.filters.allRoles') },
              ...filterOptions.roles.map((role) => ({ value: role.roleId, label: role.name })),
            ]}
          />
          <Select
            aria-label={t('sales.salesTeam.filters.allReportsTo')}
            data-testid="my-team-reports-to-select"
            value={managerId}
            onChange={(event) => setManagerId(event.target.value)}
            options={[
              { value: ALL, label: t('sales.salesTeam.filters.allReportsTo') },
              ...filterOptions.managers.map((manager) => ({
                value: manager.userId,
                label: manager.displayName,
              })),
            ]}
          />
          <Select
            aria-label={t('sales.salesTeam.filters.allStatus')}
            data-testid="my-team-status-select"
            value={status}
            onChange={(event) => setStatus(event.target.value as DirectoryStatus | '')}
            options={[
              { value: ALL, label: t('sales.salesTeam.filters.allStatus') },
              ...STATUS_FILTERS.map((code) => ({
                value: code,
                label: t(DIRECTORY_STATUS_LABEL_KEY[code]),
              })),
            ]}
          />
          {/* Always mounted so toggling filters never flips this row between one/two lines. With no
              filter active it is `invisible` + `aria-hidden` + `disabled`, so Playwright's
              `toBeVisible()` fails in the default state; QA should apply a filter first (or assert
              `toBeHidden()` / `toBeDisabled()` for the default state). The same caveat is in the
              PR's locator list. */}
          <button
            type="button"
            onClick={clearFilters}
            disabled={!anyFilter}
            aria-hidden={!anyFilter}
            data-testid="my-team-clear-filters-button"
            className={`rounded-lg border border-input-border bg-white px-3.5 py-2 text-[13px] font-semibold text-slate ${
              anyFilter ? 'cursor-pointer hover:border-border-hover hover:text-ink' : 'invisible'
            }`}
          >
            {t('sales.salesTeam.filters.clear')}
          </button>
          <span className="flex-1" />
          <span className="text-[13px] text-slate" data-testid="my-team-entries-line">
            {t('sales.salesTeam.entriesLine', {
              shown: members.length,
              total: summary?.teamSize ?? members.length,
            })}
          </span>
        </div>

        <div className="grow overflow-x-auto border-t border-divider">
          <div className={TABLE_MIN_WIDTH}>
            <div
              className={`grid ${GRID_COLUMNS} items-center gap-3 bg-input-bg px-4 py-2.5 sm:px-5`}
            >
              {[
                t('sales.salesTeam.columns.employee'),
                t('sales.salesTeam.columns.employeeId'),
                t('sales.salesTeam.columns.role'),
                t('sales.salesTeam.columns.reportsTo'),
                t('sales.salesTeam.columns.assignedBanks'),
                t('sales.salesTeam.columns.status'),
                '',
              ].map((label, index) => (
                <span
                  key={index}
                  className="whitespace-nowrap text-[11px] font-semibold tracking-[0.6px] text-table-header"
                >
                  {label}
                </span>
              ))}
            </div>

            {members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                expanded={expandedId === member.userId}
                onToggle={() =>
                  setExpandedId((current) => (current === member.userId ? null : member.userId))
                }
              />
            ))}

            {members.length === 0 &&
              (anyFilter ? (
                <TableEmptyState
                  message={t('sales.salesTeam.noResults')}
                  clearLabel={t('sales.salesTeam.filters.clear')}
                  onClear={clearFilters}
                />
              ) : (
                <TableEmptyState
                  message={t('sales.salesTeam.noMembers')}
                  description={t('sales.salesTeam.noMembersDescription')}
                  icon="users"
                />
              ))}
          </div>
        </div>

        <TableFooter
          line={t('sales.salesTeam.entriesLine', {
            shown: members.length,
            total: summary?.teamSize ?? members.length,
          })}
        />
      </section>
    </div>
  )
}

export default SalesTeamPage
